/** Service worker plumbing: registration and the (page-initiated) activation of a waiting worker. */

export type ActivateOutcome = 'activated' | 'none' | 'timeout'

export interface SwApi {
  /** Asks the browser to look for a new sw.js. Never throws. */
  update(): Promise<void>
  /** If a new worker is installed/installing, tells it to skipWaiting and waits for controllerchange. */
  activateWaiting(timeoutMs: number): Promise<ActivateOutcome>
}

export const swSupported = () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!swSupported()) return null
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' })
  } catch {
    return null
  }
}

function waitFor<T>(setup: (done: (value: T) => void) => () => void, timeoutMs: number, onTimeout: T): Promise<T> {
  return new Promise((resolve) => {
    let cleanup = () => undefined as void
    const timer = setTimeout(() => {
      cleanup()
      resolve(onTimeout)
    }, timeoutMs)
    cleanup = setup((value) => {
      clearTimeout(timer)
      cleanup()
      resolve(value)
    })
  })
}

export function createSwApi(): SwApi | null {
  if (!swSupported()) return null
  const registration = () => navigator.serviceWorker.getRegistration('/')
  return {
    async update() {
      try {
        await (await registration())?.update()
      } catch {
        /* offline or script fetch failed: the notice stays, nothing breaks */
      }
    },
    async activateWaiting(timeoutMs) {
      const reg = await registration()
      if (!reg || !navigator.serviceWorker.controller) return 'none'
      if (!reg.waiting && reg.installing) {
        const installing = reg.installing
        await waitFor<void>(
          (done) => {
            const onChange = () => {
              if (installing.state === 'installed' || installing.state === 'redundant') done()
            }
            installing.addEventListener('statechange', onChange)
            onChange()
            return () => installing.removeEventListener('statechange', onChange)
          },
          timeoutMs,
          undefined,
        )
      }
      const waiting = reg.waiting
      if (!waiting) return 'none'
      return waitFor<ActivateOutcome>(
        (done) => {
          const onChange = () => done('activated')
          navigator.serviceWorker.addEventListener('controllerchange', onChange)
          waiting.postMessage({ type: 'SKIP_WAITING' })
          return () => navigator.serviceWorker.removeEventListener('controllerchange', onChange)
        },
        timeoutMs,
        'timeout',
      )
    },
  }
}
